<template>
  <div class="documentation">
    <div class="container">
      <div class="docs-header">
        <div class="docs-header-content">
          <div class="docs-title">
            <h1>ClipShare Documentation</h1>
            <p>Complete guide for using ClipShare API and client tools</p>
          </div>
          <button @click="goHome" class="home-btn-top" title="Go to Home">
            <Icon name="home" :size="20" />
          </button>
        </div>
      </div>
      
      <div class="docs-nav">
        <a href="#mcp-integration" class="nav-link">MCP Integration</a>
        <a href="#client-tool" class="nav-link">Client Tool</a>
        <a href="#api-usage" class="nav-link">API Usage</a>
        <a href="#examples" class="nav-link">Examples</a>
      </div>

      <div id="mcp-integration" class="mcp-section">
        <h2>MCP Integration (Model Context Protocol)</h2>
        <p>Integrate ClipShare with AI development tools like Cursor IDE using Model Context Protocol (MCP). Access all ClipShare functionality through 13 powerful MCP tools.</p>
        
        <div class="mcp-overview">
          <div class="mcp-intro">
            <h3>🤖 What is MCP?</h3>
            <p>Model Context Protocol enables AI assistants to interact directly with external tools and services. With ClipShare's MCP server, you can manage clipboards, entries, and files directly from your AI coding assistant.</p>
          </div>
          
          <div class="mcp-benefits">
            <h3>✨ Benefits</h3>
            <ul>
              <li><strong>Seamless Integration</strong>: Work with clipboards directly in Cursor IDE</li>
              <li><strong>AI-Powered</strong>: Let AI assistants manage your clipboard content</li>
              <li><strong>Search Functionality</strong>: Find entries using natural language queries</li>
              <li><strong>File Management</strong>: Handle files without leaving your development environment</li>
              <li><strong>Productivity Boost</strong>: Automate clipboard operations in your workflow</li>
            </ul>
          </div>
        </div>
        
        <div class="mcp-setup">
          <h3>🚀 Quick Setup</h3>
          <div class="setup-step">
            <h4>Configure Cursor IDE</h4>
            <p>Add this configuration to your <code>.cursor/mcp.json</code> file:</p>
            <div class="code-block">
              <pre><code>{
  "mcpServers": {
    "clipshare": {
      "url": "http://127.0.0.1:8000/mcp",
      "transport": "streamable-http"
    }
  }
}</code></pre>
              <button @click="copyCommand('mcp-config')" class="copy-btn">
                <span v-if="!copyStates['mcp-config']">Copy</span>
                <span v-else class="copy-success">
                  <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                    <path fill="none" stroke="currentColor" stroke-width="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Copied
                </span>
              </button>
            </div>
          </div>
        </div>
        
        <div class="mcp-tools">
          <h3>🛠️ Available MCP Tools (13 Total)</h3>
          
          <div class="tools-grid">
            <div class="tool-category">
              <h4>📋 Clipboard Management (3 tools)</h4>
              <ul class="tool-list">
                <li><code>create_clipboard</code> - Create new clipboards</li>
                <li><code>get_clipboard_info</code> - Get clipboard details and stats</li>
                <li><code>update_clipboard</code> - Rename clipboards</li>
              </ul>
            </div>
            
            <div class="tool-category">
              <h4>📝 Entry Management (6 tools)</h4>
              <ul class="tool-list">
                <li><code>add_clipboard_entry</code> - Add text/markdown content</li>
                <li><code>list_clipboard_entries</code> - List entries with search</li>
                <li><code>get_entry</code> - Get entry content by ID</li>
                <li><code>get_entry_info</code> - Get entry metadata</li>
                <li><code>update_entry</code> - Update entry content</li>
                <li><code>delete_entry</code> - Remove entries</li>
              </ul>
            </div>
            
            <div class="tool-category">
              <h4>📁 File Management (4 tools)</h4>
              <ul class="tool-list">
                <li><code>list_clipboard_files</code> - List files in clipboard</li>
                <li><code>get_file_info</code> - Get file metadata</li>
                <li><code>get_file_download_url</code> - Get download links</li>
                <li><code>delete_file</code> - Remove files</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div class="mcp-features">
          <h3>🔍 Key Features</h3>
          <div class="feature-highlights">
            <div class="feature-highlight">
              <h4>🔎 Smart Search</h4>
              <p>Search through entry content using natural language. The <code>list_clipboard_entries</code> tool supports keyword-based fuzzy search with context highlighting.</p>
            </div>
            
            <div class="feature-highlight">
              <h4>🔄 Real-time Sync</h4>
              <p>MCP tools work with the same database as the web interface, ensuring consistent data across all access methods.</p>
            </div>
            
            <div class="feature-highlight">
              <h4>🛡️ Production Safe</h4>
              <p>MCP server runs independently on port 8000, while the web API continues on port 5001. No disruption to existing workflows.</p>
            </div>
          </div>
        </div>
        
        <div class="mcp-examples">
          <h3>💡 Usage Examples</h3>
          <div class="example-scenarios">
            <div class="scenario">
              <h4>📝 Quick Note Taking</h4>
              <p>"Add this code snippet to my dev-notes clipboard"</p>
              <small>AI will use <code>add_clipboard_entry</code> with markdown formatting</small>
            </div>
            
            <div class="scenario">
              <h4>🔍 Content Search</h4>
              <p>"Find all entries in my clipboard that mention 'API documentation'"</p>
              <small>AI will use <code>list_clipboard_entries</code> with keyword search</small>
            </div>
            
            <div class="scenario">
              <h4>📁 File Management</h4>
              <p>"Show me all files in the project-assets clipboard and get download links"</p>
              <small>AI will use <code>list_clipboard_files</code> and <code>get_file_download_url</code></small>
            </div>
            
            <div class="scenario">
              <h4>🗂️ Organization</h4>
              <p>"Create a new clipboard called 'meeting-notes' and add today's agenda"</p>
              <small>AI will use <code>create_clipboard</code> and <code>add_clipboard_entry</code></small>
            </div>
          </div>
        </div>
        
        
      </div>
      
      <div id="client-tool" class="client-tool">
        <h2>ClipShare Client Tool</h2>
        <p>Download our shell script for easy file transfers between servers:</p>
        
        <div class="download-section">
          <a href="/clipshare.sh" download class="download-btn">Download ClipShare Client</a>
          <div class="installation-steps">
            <h3>Quick Installation</h3>
            <div class="code-block">
              <pre><code>curl -sL {{ baseUrl }}/clipshare.sh -o clipshare.sh && chmod +x clipshare.sh</code></pre>
              <button @click="copyCommand('install')" class="copy-btn">
                <span v-if="!copyStates.install">Copy</span>
                <span v-else class="copy-success">
                  <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                    <path fill="none" stroke="currentColor" stroke-width="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Copied
                </span>
              </button>
            </div>
            <p class="command-description">This command downloads the script and makes it executable.</p>
            
            <h3>Create Command Shortcut</h3>
            <div class="code-block">
              <pre><code>./clipshare.sh install local</code></pre>
              <button @click="copyCommand('install-shortcut')" class="copy-btn">
                <span v-if="!copyStates['install-shortcut']">Copy</span>
                <span v-else class="copy-success">
                  <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                    <path fill="none" stroke="currentColor" stroke-width="3" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Copied
                </span>
              </button>
            </div>
            <p class="command-description">This creates a 'clipshare' command shortcut so you can use the tool from any directory without typing the full path. You can also use <code>install alias</code> to create a shell alias instead.</p>
          </div>
        </div>
        
        <div class="usage-guide">
          <h3>Client Tool Usage</h3>
          <div class="feature-list">
            <div class="feature">
              <h4>Configuration</h4>
              <div class="code-block">
                <pre><code>clipshare config {{ baseUrl }}</code></pre>
                <button @click="copyCommand('config-short')" class="copy-btn">
                  <span v-if="!copyStates['config-short']">Copy</span>
                  <span v-else class="copy-success">
                    <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                      <path fill="none" stroke="currentColor" stroke-width="3" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Copied
                  </span>
                </button>
              </div>
            </div>
            
            <div class="feature">
              <h4>Pack Files/Folders</h4>
              <div class="code-block">
                <pre><code>./clipshare.sh pack /path/to/files output.tar.gz</code></pre>
                <button @click="copyCommand('pack')" class="copy-btn">
                  <span v-if="!copyStates.pack">Copy</span>
                  <span v-else class="copy-success">
                    <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                      <path fill="none" stroke="currentColor" stroke-width="3" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Copied
                  </span>
                </button>
              </div>
            </div>
            
            <div class="feature">
              <h4>Upload Files</h4>
              <div class="code-block">
                <pre><code>./clipshare.sh upload file.tar.gz YOUR_CLIPBOARD_ID</code></pre>
                <button @click="copyCommand('script-upload')" class="copy-btn">
                  <span v-if="!copyStates['script-upload']">Copy</span>
                  <span v-else class="copy-success">
                    <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                      <path fill="none" stroke="currentColor" stroke-width="3" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Copied
                  </span>
                </button>
              </div>
            </div>
            
            <div class="feature">
              <h4>Download Files</h4>
              <div class="code-block">
                <pre><code>./clipshare.sh download FILE_ID</code></pre>
                <button @click="copyCommand('script-download')" class="copy-btn">
                  <span v-if="!copyStates['script-download']">Copy</span>
                  <span v-else class="copy-success">
                    <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                      <path fill="none" stroke="currentColor" stroke-width="3" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Copied
                  </span>
                </button>
              </div>
            </div>
            
            <div class="feature">
              <h4>List Files</h4>
              <div class="code-block">
                <pre><code>./clipshare.sh list YOUR_CLIPBOARD_ID</code></pre>
                <button @click="copyCommand('list')" class="copy-btn">
                  <span v-if="!copyStates.list">Copy</span>
                  <span v-else class="copy-success">
                    <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                      <path fill="none" stroke="currentColor" stroke-width="3" d="M5 13l4 4L19 7"></path>
                    </svg>
                    Copied
                  </span>
                </button>
              </div>
            </div>
          </div>
          
          <div class="shortcut-info">
            <h4>Using Command Shortcut</h4>
            <p>After installing the command shortcut, you can use <code>clipshare</code> instead of <code>./clipshare.sh</code> from any directory:</p>
            <div class="code-examples">
              <div class="code-example">
                <code>clipshare list my-clipboard</code>
              </div>
              <div class="code-example">
                <code>clipshare upload backup.tar.gz</code>
              </div>
              <div class="code-example">
                <code>clipshare download file-id</code>
              </div>
            </div>
          </div>
          
          <div class="tool-benefits">
            <h4>Benefits</h4>
            <ul>
              <li>Simple operation with clear commands</li>
              <li>Shows progress during uploads and downloads</li>
              <li>Built-in compression for files and directories</li>
              <li>Works on most Linux systems without additional dependencies</li>
              <li>Automatically uses original filenames when downloading</li>
              <li><strong>NEW:</strong> Create command shortcuts to use from any directory</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div id="api-usage" class="terminal-commands">
        <h2>API Usage</h2>
        <p>Use these commands to quickly upload and download files with ClipShare:</p>
        <div class="command-section">
          <h3>Upload files via terminal</h3>
          <div class="code-block">
            <pre><code>curl -F "file=@/path/to/your/file.txt" {{ baseUrl }}/api/clipboards/YOUR_CLIPBOARD_ID/files</code></pre>
            <button @click="copyCommand('curl')" class="copy-btn">
              <span v-if="!copyStates.curl">Copy</span>
              <span v-else class="copy-success">
                <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                  <path fill="none" stroke="currentColor" stroke-width="3" d="M5 13l4 4L19 7"></path>
                </svg>
                Copied
              </span>
            </button>
          </div>
          <p class="command-description">Replace <code>YOUR_CLIPBOARD_ID</code> with your clipboard ID and <code>/path/to/your/file.txt</code> with the file path you want to upload.</p>
          
          <h3>Alternative using wget</h3>
          <div class="code-block">
            <pre><code>wget --method=POST -O- --body-file=/path/to/your/file.txt \
     --header="Content-Type: multipart/form-data; boundary=boundary" \
     {{ baseUrl }}/api/clipboards/YOUR_CLIPBOARD_ID/files</code></pre>
            <button @click="copyCommand('wget')" class="copy-btn">
              <span v-if="!copyStates.wget">Copy</span>
              <span v-else class="copy-success">
                <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                  <path fill="none" stroke="currentColor" stroke-width="3" d="M5 13l4 4L19 7"></path>
                </svg>
                Copied
              </span>
            </button>
          </div>
          
          <h3>Upload multiple files</h3>
          <div class="code-block">
            <pre><code>curl -F "file=@/path/to/file1.txt" -F "file=@/path/to/file2.jpg" {{ baseUrl }}/api/clipboards/YOUR_CLIPBOARD_ID/files</code></pre>
            <button @click="copyCommand('multiple')" class="copy-btn">
              <span v-if="!copyStates.multiple">Copy</span>
              <span v-else class="copy-success">
                <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                  <path fill="none" stroke="currentColor" stroke-width="3" d="M5 13l4 4L19 7"></path>
                </svg>
                Copied
              </span>
            </button>
          </div>
          
          <h3>Download a file</h3>
          <div class="code-block">
            <pre><code>curl -O {{ baseUrl }}/api/files/FILE_ID</code></pre>
            <button @click="copyCommand('download')" class="copy-btn">
              <span v-if="!copyStates.download">Copy</span>
              <span v-else class="copy-success">
                <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                  <path fill="none" stroke="currentColor" stroke-width="3" d="M5 13l4 4L19 7"></path>
                </svg>
                Copied
              </span>
            </button>
          </div>
          <p class="command-description">
            Replace <code>FILE_ID</code> with the ID of the file you want to download. <br/>
            <strong>How to get FILE_ID</strong>: When you open a clipboard, all files will be listed with their information. 
            The FILE_ID is displayed in the browser URL when you click the "Download" button on any file. 
            Alternatively, the server response when uploading a file also includes the file ID.
          </p>
        </div>
      </div>

      <div id="examples" class="examples-section">
        <h2>Quick Examples</h2>
        <div class="example-grid">
          <div class="example-card">
            <h4>🚀 Quick Start</h4>
            <ol>
              <li>Create a new clipboard on the homepage</li>
              <li>Upload files using the web interface or API</li>
              <li>Share the clipboard URL with others</li>
              <li>Access from any device using the clipboard ID</li>
            </ol>
          </div>
          
          <div class="example-card">
            <h4>💻 Server to Server Transfer</h4>
            <ol>
              <li>Download client tool: <code>curl -sL {{ baseUrl }}/clipshare.sh -o clipshare.sh</code></li>
              <li>Make executable: <code>chmod +x clipshare.sh</code></li>
              <li>Upload: <code>./clipshare.sh upload myfile.tar.gz my-clipboard-id</code></li>
              <li>Download on target server: <code>./clipshare.sh download file-id</code></li>
            </ol>
          </div>
          
          <div class="example-card">
            <h4>📱 Cross-Platform Sharing</h4>
            <ol>
              <li>Create clipboard and upload files from your computer</li>
              <li>Open the same clipboard URL on your phone/tablet</li>
              <li>Download or preview files directly in the browser</li>
              <li>Edit markdown content with live preview</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getFrontendUrl } from '@/config';
import Icon from '@/components/Icon.vue';

export default {
  name: 'Documentation',
  components: {
    Icon
  },
  data() {
    return {
      baseUrl: getFrontendUrl(),
      copyStates: {
        curl: false,
        wget: false,
        multiple: false,
        download: false,
        install: false,
        'install-shortcut': false,
        config: false,
        'config-short': false,
        pack: false,
        'script-upload': false,
        'script-download': false,
        list: false,
        'mcp-install': false,
        'mcp-start': false,
        'mcp-config': false
      }
    }
  },
  methods: {
    copyCommand(type) {
      let command = '';
      
      switch(type) {
        case 'curl':
          command = `curl -F "file=@/path/to/your/file.txt" ${this.baseUrl}/api/clipboards/YOUR_CLIPBOARD_ID/files`;
          break;
        case 'wget':
          command = `wget --method=POST -O- --body-file=/path/to/your/file.txt \\\n     --header="Content-Type: multipart/form-data; boundary=boundary" \\\n     ${this.baseUrl}/api/clipboards/YOUR_CLIPBOARD_ID/files`;
          break;
        case 'multiple':
          command = `curl -F "file=@/path/to/file1.txt" -F "file=@/path/to/file2.jpg" ${this.baseUrl}/api/clipboards/YOUR_CLIPBOARD_ID/files`;
          break;
        case 'download':
          command = `curl -O ${this.baseUrl}/api/files/FILE_ID`;
          break;
        case 'install':
          command = `curl -sL ${this.baseUrl}/clipshare.sh -o clipshare.sh && chmod +x clipshare.sh`;
          break;
        case 'install-shortcut':
          command = `./clipshare.sh install local`;
          break;
        case 'config-short':
          command = `clipshare config ${this.baseUrl}`;
          break;
        case 'config':
          command = `./clipshare.sh config ${this.baseUrl}`;
          break;
        case 'pack':
          command = `./clipshare.sh pack /path/to/files output.tar.gz`;
          break;
        case 'script-upload':
          command = `./clipshare.sh upload file.tar.gz YOUR_CLIPBOARD_ID`;
          break;
        case 'script-download':
          command = `./clipshare.sh download FILE_ID`;
          break;
        case 'list':
          command = `./clipshare.sh list YOUR_CLIPBOARD_ID`;
          break;
        case 'mcp-install':
          command = `pip install mcp`;
          break;
        case 'mcp-start':
          command = `cd backend && python mcp_server.py`;
          break;
        case 'mcp-config':
          command = `{
  "mcpServers": {
    "clipshare": {
      "url": "http://127.0.0.1:8000/mcp",
      "transport": "streamable-http"
    }
  }
}`;
          break;
      }
      
      navigator.clipboard.writeText(command)
        .then(() => {
          this.copyStates[type] = true;
          
          setTimeout(() => {
            this.copyStates[type] = false;
          }, 2000);
        })
        .catch(err => {
          console.error('Failed to copy command', err);
        });
    },

    goHome() {
      this.$router.push('/');
    }
  },
  mounted() {
    // Handle hash navigation
    if (this.$route.hash) {
      this.$nextTick(() => {
        const element = document.querySelector(this.$route.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }
  }
}
</script>

<style scoped>
.documentation {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
}

.container {
  background-color: white;
  border-radius: 8px;
  padding: 40px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.docs-header {
  margin-bottom: 40px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
}

.docs-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.docs-title {
  text-align: center;
  flex: 1;
}

.docs-title h1 {
  color: #2c3e50;
  font-weight: 700;
  font-size: 2.5rem;
  margin-bottom: 10px;
}

.docs-title p {
  color: #666;
  font-size: 1.2rem;
}

.home-btn-top {
  padding: 8px;
  border: none;
  border-radius: 8px;
  background-color: #42b983;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  box-shadow: 0 2px 6px rgba(66, 185, 131, 0.2);
}

.home-btn-top:hover {
  background-color: #36996b;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(66, 185, 131, 0.3);
}

.docs-nav {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-bottom: 40px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.nav-link {
  color: #42b983;
  text-decoration: none;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.nav-link:hover {
  background: #42b983;
  color: white;
  text-decoration: none;
}

.examples-section {
  margin-top: 40px;
  border-top: 1px solid #eee;
  padding-top: 30px;
}

.example-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
  margin-top: 20px;
}

.example-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 25px;
  border-left: 4px solid #42b983;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.example-card h4 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.1rem;
}

.example-card ol {
  color: #555;
  line-height: 1.6;
  padding-left: 20px;
}

.example-card li {
  margin-bottom: 8px;
}

.example-card code {
  background: #e9ecef;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.9rem;
}

/* Reuse styles from Home.vue */
h2 {
  color: #2c3e50;
  font-size: 1.8rem;
  margin-bottom: 20px;
  margin-top: 40px;
  font-weight: 600;
}

h3 {
  color: #2c3e50;
  font-size: 1.3rem;
  margin-top: 30px;
  margin-bottom: 15px;
  font-weight: 600;
}

h4 {
  color: #2c3e50;
  font-size: 1.1rem;
  margin-top: 20px;
  margin-bottom: 10px;
  font-weight: 600;
}

.client-tool {
  margin-top: 20px;
}

.download-section {
  background-color: #f0f7f4;
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 20px;
  border-left: 4px solid #42b983;
}

.download-btn {
  display: inline-block;
  padding: 12px 24px;
  background-color: #42b983;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 600;
  margin-bottom: 20px;
  transition: background-color 0.3s;
}

.download-btn:hover {
  background-color: #3aa876;
  text-decoration: none;
  color: white;
}

.installation-steps {
  margin-top: 20px;
}

.usage-guide {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.feature-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.feature {
  background-color: #fff;
  border-radius: 6px;
  padding: 15px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.tool-benefits ul {
  padding-left: 20px;
  color: #555;
  line-height: 1.6;
}

.tool-benefits li {
  margin-bottom: 8px;
}

.terminal-commands {
  margin-top: 40px;
  border-top: 1px solid #eee;
  padding-top: 20px;
}

.command-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.code-block {
  position: relative;
  background-color: #1a1a1a;
  border-radius: 6px;
  padding: 20px;
  margin: 15px 0;
  overflow-x: auto;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #42b983;
}

.code-block pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.code-block code {
  color: #4caf50;
  font-family: 'JetBrains Mono', 'Fira Code', 'Courier New', monospace;
  font-size: 15px;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
  line-height: 1.5;
  font-weight: 600;
}

.copy-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 6px 12px;
  font-size: 13px;
  background-color: #2c3e50;
  color: white;
  border: none;
  border-radius: 4px;
  opacity: 0.8;
  transition: opacity 0.3s, background-color 0.3s;
  min-width: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.copy-success {
  color: #4caf50;
  display: flex;
  align-items: center;
  gap: 4px;
}

.checkmark {
  stroke: #4caf50;
  animation: checkmark 0.3s ease-in-out forwards;
}

@keyframes checkmark {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.command-description {
  font-size: 15px;
  color: #555;
  margin: 15px 0 25px;
  line-height: 1.6;
  padding-left: 10px;
  border-left: 3px solid #e0e0e0;
}

code {
  /* background: #f4f4f4; */
  padding: 2px 6px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  color: #e83e8c;
  font-weight: 600;
}

.shortcut-info {
  background-color: #f0f7f4;
  border-radius: 6px;
  padding: 20px;
  margin-top: 25px;
  margin-bottom: 25px;
  border-left: 4px solid #3498db;
}

.code-examples {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 15px;
}

.code-example {
  background-color: #f0f0f0;
  border-radius: 4px;
  padding: 8px 12px;
  font-family: 'JetBrains Mono', 'Fira Code', 'Courier New', monospace;
  font-size: 14px;
}

@media (max-width: 768px) {
  .feature-list {
    grid-template-columns: 1fr;
  }
  
  .docs-nav {
    flex-direction: column;
    gap: 10px;
    text-align: center;
  }
  
  .example-grid {
    grid-template-columns: 1fr;
  }
  
  .docs-header-content {
    flex-direction: column;
    gap: 20px;
  }
  
  .docs-title h1 {
    font-size: 2rem;
  }
}

/* MCP Section Styles */
.mcp-section {
  margin-top: 40px;
  border-top: 1px solid #eee;
  padding-top: 30px;
}

.mcp-overview {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin: 25px 0;
}

.mcp-intro,
.mcp-benefits {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.mcp-benefits ul {
  padding-left: 20px;
  color: #555;
  line-height: 1.6;
}

.mcp-benefits li {
  margin-bottom: 8px;
}

.mcp-setup {
  background: #f0f7f4;
  border-radius: 8px;
  padding: 30px;
  margin: 25px 0;
  border-left: 4px solid #42b983;
}

.setup-step {
  margin-bottom: 25px;
}

.setup-step:last-child {
  margin-bottom: 0;
}

.tools-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
  margin-top: 20px;
}

.tool-category {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border-left: 4px solid #42b983;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.tool-category h4 {
  color: #2c3e50;
  margin-bottom: 15px;
  font-size: 1.1rem;
}

.tool-list {
  list-style: none;
  padding: 0;
}

.tool-list li {
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
  color: #555;
}

.tool-list li:last-child {
  border-bottom: none;
}

.tool-list code {
  background: #e9ecef;
  padding: 2px 6px;
  border-radius: 3px;
  color: #42b983;
  font-weight: 600;
}

.feature-highlights {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.feature-highlight {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  border-left: 4px solid #3498db;
}

.feature-highlight h4 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1rem;
}

.example-scenarios {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.scenario {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border-left: 4px solid #ff6b6b;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.scenario h4 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1rem;
}

.scenario p {
  font-style: italic;
  color: #666;
  margin-bottom: 8px;
}

.scenario small {
  color: #888;
  font-size: 0.85rem;
}

.troubleshooting-tips {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 25px;
  margin-top: 20px;
}

.tip {
  background: #fff3cd;
  border-radius: 8px;
  padding: 20px;
  border-left: 4px solid #ffc107;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.tip h4 {
  color: #856404;
  margin-bottom: 15px;
  font-size: 1rem;
}

.tip ul {
  padding-left: 20px;
  color: #856404;
  line-height: 1.6;
}

.tip li {
  margin-bottom: 8px;
}

@media (max-width: 768px) {
  .mcp-overview {
    grid-template-columns: 1fr;
  }
  
  .tools-grid {
    grid-template-columns: 1fr;
  }
  
  .feature-highlights {
    grid-template-columns: 1fr;
  }
  
  .example-scenarios {
    grid-template-columns: 1fr;
  }
  
  .troubleshooting-tips {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .container {
    padding: 20px 15px;
  }
  
  .code-block {
    padding: 15px 10px;
  }
  
  .code-block code {
    font-size: 13px;
  }
  
  .docs-header h1 {
    font-size: 2rem;
  }
}
</style>
